import BaseComponent from '@utils/base.js';
import tag from '@utils/utilTags.js';
import ControlBtn from '@components/controlBtn/controlBtn.js';
import audio from '@components/audioComponent/audio.js';
import timer from '@components/timer/timer.js';
import storage from '@app/storage.js';

import * as styles from './dashboard.module.scss';

const CONTROLS = {
  reset: 'Reset_Game',
  save: 'Save_Game',
  continue: 'Continue_Last_Game',
  change: 'Change_Nonogram',
  random: 'Random_Game',
  results: 'Results',
  solution: 'Solution',
};

const THEME_DARK = 'Theme_Dark';
const THEME_LIGHT = 'Theme_Light';

/** @extends {BaseComponent<HTMLDivElement>} */
export default class Dashboard extends BaseComponent {
  /** @type {ControlBtn} */
  themeBtn;
  /** @type {boolean} */
  isDarkTheme = false;

  constructor() {
    super({ classes: [styles.dashboard] });
    const btnBlock = tag.div({ classes: [styles.buttonBlock] });
    const themeBtn = this.createThemeBtn();
    const controls = this.createControls();
    audio.control.addClasses(styles.button);
    btnBlock.appendChildren(timer, audio.control, themeBtn, ...controls);

    this.appendSingle(btnBlock);

    window.addEventListener(
      'custom:applyTheme',
      /** @type {EventListener} */ (this.applyTheme)
    );
  }

  /**
   * Returns an array of control buttons.
   * @returns {BaseComponent<HTMLButtonElement>[]}
   * @private
   */
  createControls() {
    const controls = Object.entries(CONTROLS).map(([eventName, text]) => {
      const controlBtn = new ControlBtn({
        text: text,
        classes: [styles.button],
      });
      controlBtn.addListener('click', () => {
        /** @type {CustomEvent<MachineCustomEventDetail>} */
        const btnEvent = new CustomEvent('custom:changestate', {
          detail: { action: eventName },
        });
        window.dispatchEvent(btnEvent);
      });
      return controlBtn;
    });

    return controls;
  }

  /**
   * Creates a theme toggle button.
   * @returns {ControlBtn}
   */
  createThemeBtn() {
    this.themeBtn = new ControlBtn({
      classes: [styles.themeBtn, styles.button],
      text: THEME_LIGHT,
    });

    this.themeBtn.addListener('click', () => {
      this.isDarkTheme = !this.isDarkTheme;
      const text = this.isDarkTheme ? THEME_DARK : THEME_LIGHT;
      this.themeBtn.setText(text);
      document.body.classList.toggle('theme-dark');
      storage.setItem('isDarkTheme', this.isDarkTheme);
    });

    return this.themeBtn;
  }

  /**
   * Applies the theme provided by the controller.
   * @param {CustomEvent<{isDarkTheme: boolean}>} e
   * @returns {void}
   */
  applyTheme = (e) => {
    this.isDarkTheme = e.detail.isDarkTheme;

    if (this.isDarkTheme) {
      document.body.classList.add('theme-dark');
      this.themeBtn.setText(THEME_DARK);
    } else {
      document.body.classList.remove('theme-dark');
      this.themeBtn.setText(THEME_LIGHT);
    }
  };
}
